import React, { useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';

import { Div, Image, P, SliderArrowButton, SliderDotsWrap, SliderDot, SliderSlide } from '../../styles/styles';
import RightArrow from '../../images/right-arrow.svg';
import { WELCOME_SLIDER_SLIDES, WELCOME_SLIDER_IMAGE_SOURCES } from './welcomeSliderSlides';
import { preloadAdjacentImages } from '../../utils/landingImagePreload';

const AUTO_PLAY_MS = 5000;

const WelcomeSlider = ({ onPrimaryBackground = false }) => {
  const [index, setIndex] = useState(0);
  const isDesktop = useMediaQuery({ minWidth: '1023px' });
  const isMobile = useMediaQuery({ maxWidth: '766px' });
  const { t, i18n } = useTranslation('registration');

  const slideCount = WELCOME_SLIDER_SLIDES.length;
  const current = WELCOME_SLIDER_SLIDES[index];

  const changeSlide = useCallback(
    (dir) => {
      setIndex((prev) => (prev + dir + slideCount) % slideCount);
    },
    [slideCount],
  );

  const goToSlide = useCallback((nextIndex) => {
    if (nextIndex < 0 || nextIndex >= slideCount) return;
    setIndex(nextIndex);
  }, [slideCount]);

  const handlers = useSwipeable({
    onSwipedLeft: () => changeSlide(1),
    onSwipedRight: () => changeSlide(-1),
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  useEffect(() => {
    if (!onPrimaryBackground) return;
    preloadAdjacentImages(WELCOME_SLIDER_IMAGE_SOURCES, index);
  }, [index, onPrimaryBackground]);

  useEffect(() => {
    if (slideCount <= 1) return undefined;

    const timer = window.setInterval(() => {
      changeSlide(1);
    }, AUTO_PLAY_MS);

    return () => window.clearInterval(timer);
  }, [changeSlide, slideCount, index, i18n.language]);

  useEffect(() => {
    if (!isDesktop) return undefined;

    const onKey = (event) => {
      if (event.key === 'ArrowRight') {
        changeSlide(1);
      } else if (event.key === 'ArrowLeft') {
        changeSlide(-1);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDesktop, changeSlide]);

  if (!current) return null;

  const titleKey = `slider.${current.id}.title`;
  const textKey = `slider.${current.id}.text`;

  return (
    <Div {...handlers} gap="15px" width="100%">
      <SliderSlide>
        {!isMobile && (
          <>
            <SliderArrowButton left aria-label="Previous slide" onClick={() => changeSlide(-1)}>
              <img src={RightArrow} alt="" />
            </SliderArrowButton>
            <SliderArrowButton aria-label="Next slide" onClick={() => changeSlide(1)}>
              <img src={RightArrow} alt="" />
            </SliderArrowButton>
          </>
        )}

        <Image
          src={current.image}
          alt=""
          decoding="async"
          style={{ height: isDesktop ? 'clamp(140px, 22vh, 168px)' : 'clamp(120px, 22vh, 150px)' }}
        />

        <Div
          gap="10px"
          style={{
            minHeight: isDesktop ? '72px' : '56px',
            marginTop: 10,
          }}
        >
          <P fontWeight="500">{t(titleKey)}</P>
          <P fontSize="14px">{t(textKey)}</P>
        </Div>
      </SliderSlide>

      <SliderDotsWrap aria-label="slider pagination">
        {WELCOME_SLIDER_SLIDES.map((slide, slideIndex) => (
          <SliderDot
            key={slide.id}
            onClick={() => goToSlide(slideIndex)}
            role="button"
            aria-label={`Go to slide ${slideIndex + 1}`}
            aria-current={slideIndex === index ? 'true' : 'false'}
            active={slideIndex === index}
            onPrimary={onPrimaryBackground}
          />
        ))}
      </SliderDotsWrap>
    </Div>
  );
};

export default WelcomeSlider;
